import type { FC } from 'react';
import React from 'react';
import type { StyledProps } from '@compiled/react';
import { styled } from '@compiled/react';

import { token, useThemeObserver } from '@atlaskit/tokens';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const NoteActionsWrapper: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled.div({
	marginTop: token('space.100'),
	font: token('font.body.small'),
	display: 'flex',
	alignItems: 'baseline',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-anchor -- To migrate as part of go/ui-styling-standard
const ArchivePageActionLinkAnchor = styled.a<{
	color: string;
	isDisabled: boolean;
}>({
	flexShrink: 0,
	display: 'inline-block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: (props) => (props?.isDisabled ? 'default' : 'pointer'),
	fontWeight: token('font.weight.bold'),
	textDecoration: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		/* Must override global, somewhat specific styles that make all links blue. span here targets the FormattedMessage inside the link */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) => props.color,
	},
	'&:hover': {
		textDecoration: 'none',
	},
});

export const ArchivePageActionLink: FC<React.ComponentProps<'a'> & { isDisabled?: boolean }> = ({
	isDisabled = false,
	onClick,
	children,
	...props
}) => {
	const theme = useThemeObserver();

	// During dark-mode tokenization in CCECO-700 we were unable to identify an appropriate
	// design-system token to use to represent this color in such a way that had sufficient
	// contrast with the background colors available in both light and dark modes. As a
	// result we're leaving this as custom for now but adding it as a request to our design
	// team to identify the best path forward for this color.

	const getColor = () => {
		if (theme.colorMode === 'dark') {
			return isDisabled ? '#42526E' : '#0052CC';
		} else {
			return isDisabled ? '#97A0AF' : '#B3D4FF';
		}
	};

	const onClickInternal = (event: React.MouseEvent<HTMLAnchorElement>) => {
		if (!isDisabled) {
			onClick?.(event);
		}
	};

	return (
		<ArchivePageActionLinkAnchor
			color={getColor()}
			isDisabled={isDisabled}
			aria-disabled={isDisabled}
			onClick={onClickInternal}
			{...props}
		>
			{children}
		</ArchivePageActionLinkAnchor>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spacer = styled.div({
	margin: `0 ${token('space.100')}`,
});
export const HorizontalSeparator = (): React.JSX.Element => <Spacer>•</Spacer>;
